import { Component, h, Element, Prop, State } from '@stencil/core';
import axios from 'axios';
/**
 * COMPNAME is for creating a container with an Image, Title, Text, and a CTA.
 *
 * <em>For More Information:</em>
 * AEM Component- ui.apps/src/main/content/jcr_root/apps/brand/aafp/components/content/aafp-tag-filter-dam-list
 * Sling Model- core/src/main/java/org/aafp/aem/core/models/AafpTagFilterDAMListModel.java
 */
@Component({
  tag: 'aafp-tag-filter-dam-list',
  shadow: false,
})
export class AafpTagFilterDAMList {
  @Element() element: HTMLElement;
  /**
   * For storing the path to the resource that will be populated
   */
  @Prop() resourcePath: string;
  /**
   * For storing the text that will be used to filter the results
   */
  @Prop() filterResultsText: string;
  /**
   * For showing all of the results
   */
  @Prop() showResults?: boolean;

  @State() activeSearchFilters: string = '';
  filterResultsContainer: HTMLElement;
  filterResultsTextDiv: HTMLElement;
  noResultsDiv: HTMLElement;
  resultsDiv: HTMLElement;
  spinnerDiv: HTMLElement;
  selectDropdowns: HTMLSelectElement[];
  HIDDEN = 'adl-tag-filter-dam-list--hidden';
  ACTIVE_DROPDOWN = 'adl-dropdown__select--active';
  SPINNER_HIDDEN: string = 'adl-tag-filter-dam-list__spinner--hidden';
  SPINNER: string = 'adl-tag-filter-dam-list__spinner';
  FILTER_RESULTS_HIDDEN: string = 'adl-tag-filter-dam-list__filter-results--hidden';
  FILTER_RESULTS: string = 'adl-tag-filter-dam-list__filter-results';
  searchVals = new Map();
  searchValsText = new Map();

  private async getFilterResults(path: string) {
    try {
      this.filterResultsContainer.querySelectorAll('aafp-link-pile').forEach((element) => element.remove());
      this.spinnerDiv.classList.toggle(this.SPINNER_HIDDEN);
      if (!this.showResults) {
        this.resultsDiv.classList.toggle(this.FILTER_RESULTS_HIDDEN);
        this.filterResultsContainer.classList.remove(this.HIDDEN);
        this.filterResultsTextDiv.classList.remove(this.FILTER_RESULTS_HIDDEN);
        this.resultsDiv.classList.toggle(this.FILTER_RESULTS_HIDDEN);
      }
      this.noResultsDiv.classList.add(this.HIDDEN);
      const { data }: { data: string } = await axios.get(path);
      const html = data.match(/<aafp-link-pile([\s\S]*?)<\/aafp-link-pile>/gm);
      if (html && html.length > 0) {
        this.resultsDiv.innerHTML = html[0];
      } else {
        this.noResultsDiv.classList.toggle(this.HIDDEN);
      }

      const resultTotal = data.match(/filter-results-text=\"([\s\S]*?)\"/gm);
      if (resultTotal && resultTotal.length > 0) {
        let resultTotalStr = resultTotal[0].replace('filter-results-text="', '');
        if (resultTotalStr.length > 0) {
          resultTotalStr = resultTotalStr.substring(0, resultTotalStr.length - 1);
          this.filterResultsText = resultTotalStr;
        }
      }

      this.activeSearchFilters = this.getActiveSearchFilters();
      this.spinnerDiv.classList.toggle(this.SPINNER_HIDDEN);
    } catch (e) {
      return;
    }
  }

  private getActiveSearchFilters() {
    let queryStr = '';
    for (const [key, value] of this.searchVals.entries()) {
      const searchTextVal = this.searchValsText.get(`${key}-${value}`);
      if (searchTextVal.trim().length > 0) {
        if (queryStr.length > 0) {
          queryStr += ', ';
        }
        queryStr += searchTextVal;
      }
    }

    return queryStr;
  }

  private clearResultsHTML() {
    this.resultsDiv.innerHTML = '';
  }

  private onChangeSelectOption = (event: Event) => {
    const selectEl = event.target as HTMLSelectElement;
    this.searchVals.set(selectEl.id, selectEl.value);
    this.searchValsText.set(`${selectEl.id}-${selectEl.value}`, selectEl[selectEl.selectedIndex].innerText.trim());
  };

  private onClick = (event: Event) => {
    event.preventDefault();
    let componentPath = this.resourcePath;
    this.clearResultsHTML();
    const updatePath = (value: string, key: string) => {
      if (!value) return;
      if (/\?/.test(componentPath)) {
        componentPath = `${componentPath}&${key}=${value}`;
      } else {
        componentPath = `${componentPath}?${key}=${value}`;
      }
    };
    this.searchVals.forEach((v: string, k: string) => updatePath(v, k));
    this.getFilterResults(componentPath);
  };

  componentDidLoad() {
    const button = this.element.querySelector('button');
    if (button) {
      button.addEventListener('click', this.onClick);
    }
    this.selectDropdowns = Array.from(this.element.querySelectorAll('select'));
    this.selectDropdowns.forEach((selectEl: HTMLSelectElement) => {
      selectEl.addEventListener('change', this.onChangeSelectOption);
    });
  }

  render() {
    if (!this.showResults) {
      return (
        <div class="adl-tag-filter-dam-list adl-space__sm__m-b-sm">
          <slot name="dropdown-filters" />
          <div ref={(el) => (this.spinnerDiv = el as HTMLElement)} class={`${this.SPINNER} ${this.SPINNER_HIDDEN}`}>
            <aafp-spinner-inline action-id="tag-filter-dam-list-spinner" size="lg">
              {' '}
            </aafp-spinner-inline>
          </div>
          <div ref={(el) => (this.filterResultsContainer = el as HTMLElement)} class="adl-tag-filter-dam-list--hidden">
            <div
              ref={(el) => (this.filterResultsTextDiv = el as HTMLElement)}
              class="adl-tag-filter-dam-list__filter-results-text adl-tag-filter-dam-list__filter-results--hidden"
            >
              <div class="adl-h3 adl-tag-filter-dam-list__heading">{this.filterResultsText}</div>
              <div class="adl-tag-filter-dam-list__active-filters adl-body">{this.activeSearchFilters}</div>
              <div
                ref={(el) => (this.noResultsDiv = el as HTMLElement)}
                class={`adl-tag-filter-dam-list__no-results adl-body ${this.HIDDEN}`}
              >
                No results found
              </div>
            </div>
            <slot name="filter-results"></slot>
            <div ref={(el) => (this.resultsDiv = el as HTMLElement)} class={`${this.FILTER_RESULTS}`}></div>
          </div>
        </div>
      );
    }
    return (
      <div class="adl-tag-filter-dam-list adl-space__sm__m-b-sm">
        <slot name="dropdown-filters" />
        <div ref={(el) => (this.spinnerDiv = el as HTMLElement)} class={`${this.SPINNER} ${this.SPINNER_HIDDEN}`}>
          <aafp-spinner-inline action-id="tag-filter-dam-list-spinner" size="lg">
            {' '}
          </aafp-spinner-inline>
        </div>
        <div ref={(el) => (this.filterResultsContainer = el as HTMLElement)}>
          <div
            ref={(el) => (this.filterResultsTextDiv = el as HTMLElement)}
            class="adl-tag-filter-dam-list__filter-results-text"
          >
            <div class="adl-h3 adl-tag-filter-dam-list__heading">{this.filterResultsText}</div>
            <div class="adl-tag-filter-dam-list__active-filters adl-body">{this.activeSearchFilters}</div>
            <div
              ref={(el) => (this.noResultsDiv = el as HTMLElement)}
              class={`adl-tag-filter-dam-list__no-results adl-body ${this.HIDDEN}`}
            >
              No results found
            </div>
          </div>
          <slot name="filter-results"></slot>
          <div ref={(el) => (this.resultsDiv = el as HTMLElement)} class={`${this.FILTER_RESULTS}`}></div>
        </div>
      </div>
    );
  }
}
